/**
 * Achievement catalog.
 *
 * The catalog itself lives in code — the DB only stores per-user
 * unlock rows in `achievement_unlocks`. Rationale:
 *
 *   - Adding/renaming a badge is one PR; no migration, no async
 *     deploy ordering between server and client.
 *   - Check functions are pure TypeScript, so they're trivially
 *     testable and ship with full type safety.
 *   - Catalog renders identically in the app, in marketing/email
 *     materials, and in /achievements without a DB round-trip.
 *
 * Tradeoff: an unlock id that's been removed from the catalog still
 * exists in the DB. The renderer falls back to a generic "Legacy
 * achievement" tile — see `lookupAchievement(id)` below.
 *
 * To add a new badge:
 *   1. Append to ACHIEVEMENTS with a stable id.
 *   2. Implement `check(stats)` — pure function over UserStats.
 *   3. Tests in achievements.test.ts will catch obvious mistakes
 *      (missing id, duplicate id, negative xp).
 */

export type AchievementCategory =
  | "streak"
  | "volume"
  | "accuracy"
  | "atsa"
  | "block_prep"
  | "trainer"
  | "engagement"
  | "milestone";

/**
 * Snapshot of everything a check function might look at. Computed
 * once per user per award-engine run. Keep fields cheap to source —
 * we don't want a query per check.
 */
export interface UserStats {
  /** Current streak in consecutive UTC days. */
  streakDays: number;
  /** All-time longest streak. */
  longestStreak: number;
  /** Total review rows (lifetime). */
  totalReviews: number;
  /** Bookmarked card count. */
  bookmarkedCards: number;
  /** Effective tier. Used for "founder" / tier-locked badges. */
  tier: "free" | "trainee" | "pro" | "institution" | "lifetime";
  /** Current XP. Drives level-milestone badges. */
  xp: number;
  /** Block-prep attempts that have been submitted (any score). */
  blockPrepSubmitted: number;
  /** Distinct preset ids the user has attempted at least once. */
  blockPrepDistinctPresets: number;
  /** Best score across any block-prep attempt (0–1). 0 if none. */
  blockPrepBestScore: number;
  /** Distinct ATSA modules with at least one submitted attempt. */
  atsaDistinctModules: number;
  /** Total ATSA submitted attempts. */
  atsaSubmitted: number;
  /** Best score across mock_full attempts (0–1). 0 if none. */
  atsaMockBestScore: number;
  /** Best score across any ATSA module attempt (0–1). 0 if none. */
  atsaAnyBestScore: number;
  /** Total phraseology trainer attempts (lifetime). */
  trainerAttempts: number;
  /** Phraseology attempts with no forbidden hits. */
  trainerCleanAttempts: number;
  /** Local UTC hour of the latest review (0–23). */
  lastReviewLocalHour: number | null;
  /** True if the user has reviews on both Saturday and Sunday this calendar week. */
  reviewedBothWeekendDays: boolean;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: AchievementCategory;
  /** XP credited at unlock time. */
  xp: number;
  /** Single emoji or short icon glyph. Kept narrow so the badge tile is consistent. */
  icon: string;
  /** Hidden from the gallery until unlocked. Used for surprise/discovery badges. */
  hidden?: boolean;
  /** Pure predicate over the user's stats snapshot. */
  check: (stats: UserStats) => boolean;
}

export const ACHIEVEMENTS: Achievement[] = [
  // ── Streak ───────────────────────────────────────────────────────
  {
    id: "streak_3",
    name: "Wheels up",
    description: "Three days of study in a row.",
    category: "streak",
    xp: 50,
    icon: "✈️",
    check: (s) => s.streakDays >= 3 || s.longestStreak >= 3,
  },
  {
    id: "streak_7",
    name: "First week solid",
    description: "Seven-day streak.",
    category: "streak",
    xp: 100,
    icon: "📅",
    check: (s) => s.streakDays >= 7 || s.longestStreak >= 7,
  },
  {
    id: "streak_14",
    name: "Half a block",
    description: "Two-week streak — about half a Block 1.",
    category: "streak",
    xp: 200,
    icon: "🛬",
    check: (s) => s.streakDays >= 14 || s.longestStreak >= 14,
  },
  {
    id: "streak_30",
    name: "Block-deep",
    description: "30-day streak.",
    category: "streak",
    xp: 500,
    icon: "🗓️",
    check: (s) => s.streakDays >= 30 || s.longestStreak >= 30,
  },
  {
    id: "streak_100",
    name: "Triple-digit days",
    description: "100 consecutive study days.",
    category: "streak",
    xp: 1500,
    icon: "💯",
    check: (s) => s.streakDays >= 100 || s.longestStreak >= 100,
  },
  {
    id: "streak_365",
    name: "Around the dial",
    description: "A full year of daily study.",
    category: "streak",
    xp: 5000,
    icon: "🌅",
    hidden: true,
    check: (s) => s.streakDays >= 365 || s.longestStreak >= 365,
  },

  // ── Review volume ────────────────────────────────────────────────
  {
    id: "reviews_10",
    name: "Cleared for taxi",
    description: "First ten cards reviewed.",
    category: "volume",
    xp: 25,
    icon: "🟢",
    check: (s) => s.totalReviews >= 10,
  },
  {
    id: "reviews_100",
    name: "Cleared for takeoff",
    description: "100 reviews complete.",
    category: "volume",
    xp: 100,
    icon: "🛫",
    check: (s) => s.totalReviews >= 100,
  },
  {
    id: "reviews_500",
    name: "Climb-out",
    description: "500 reviews complete.",
    category: "volume",
    xp: 250,
    icon: "⛅",
    check: (s) => s.totalReviews >= 500,
  },
  {
    id: "reviews_1000",
    name: "Cruise altitude",
    description: "One thousand reviews.",
    category: "volume",
    xp: 500,
    icon: "🛩️",
    check: (s) => s.totalReviews >= 1000,
  },
  {
    id: "reviews_5000",
    name: "Heavy",
    description: "Five thousand reviews.",
    category: "volume",
    xp: 2000,
    icon: "🛬",
    check: (s) => s.totalReviews >= 5000,
  },
  {
    id: "reviews_10000",
    name: "Senior controller",
    description: "Ten thousand reviews logged.",
    category: "volume",
    xp: 5000,
    icon: "🎖️",
    check: (s) => s.totalReviews >= 10000,
  },

  // ── Block Prep ───────────────────────────────────────────────────
  {
    id: "block_prep_first",
    name: "Block test rehearsal",
    description: "Complete your first Block Prep exam.",
    category: "block_prep",
    xp: 50,
    icon: "📝",
    check: (s) => s.blockPrepSubmitted >= 1,
  },
  {
    id: "block_prep_pass",
    name: "Pass mark",
    description: "Score 70% or better on any Block Prep exam.",
    category: "block_prep",
    xp: 150,
    icon: "✅",
    check: (s) => s.blockPrepBestScore >= 0.7,
  },
  {
    id: "block_prep_master",
    name: "Block master",
    description: "Score 90% or better on any Block Prep exam.",
    category: "block_prep",
    xp: 250,
    icon: "🏅",
    check: (s) => s.blockPrepBestScore >= 0.9,
  },
  {
    id: "block_prep_breadth",
    name: "Across the boards",
    description: "Complete exams across three different presets.",
    category: "block_prep",
    xp: 200,
    icon: "🧭",
    check: (s) => s.blockPrepDistinctPresets >= 3,
  },

  // ── ATSA ─────────────────────────────────────────────────────────
  {
    id: "atsa_first",
    name: "Aptitude rehearsal",
    description: "Complete your first ATSA module.",
    category: "atsa",
    xp: 50,
    icon: "🧠",
    check: (s) => s.atsaSubmitted >= 1,
  },
  {
    id: "atsa_all_8",
    name: "Eight for eight",
    description: "Submit attempts on every ATSA module.",
    category: "atsa",
    xp: 300,
    icon: "🎯",
    check: (s) => s.atsaDistinctModules >= 8,
  },
  {
    id: "atsa_mock_pass",
    name: "Mock-test ready",
    description: "Score 80%+ on the full timed ATSA mock.",
    category: "atsa",
    xp: 400,
    icon: "🛡️",
    check: (s) => s.atsaMockBestScore >= 0.8,
  },
  {
    id: "atsa_perfect_module",
    name: "Module perfect",
    description: "Score 100% on any single ATSA module.",
    category: "atsa",
    xp: 200,
    icon: "💎",
    check: (s) => s.atsaAnyBestScore >= 1,
  },

  // ── Trainer ──────────────────────────────────────────────────────
  {
    id: "trainer_first",
    name: "Comms check",
    description: "Submit your first phraseology readback.",
    category: "trainer",
    xp: 25,
    icon: "📻",
    check: (s) => s.trainerAttempts >= 1,
  },
  {
    id: "trainer_10",
    name: "Standard ten",
    description: "Ten phraseology attempts logged.",
    category: "trainer",
    xp: 100,
    icon: "🎙️",
    check: (s) => s.trainerAttempts >= 10,
  },
  {
    id: "trainer_clean_5",
    name: "Clean comms",
    description: "Five attempts with no forbidden phrases.",
    category: "trainer",
    xp: 150,
    icon: "🧼",
    check: (s) => s.trainerCleanAttempts >= 5,
  },
  {
    id: "trainer_50",
    name: "Frequency familiar",
    description: "Fifty phraseology attempts logged.",
    category: "trainer",
    xp: 300,
    icon: "📡",
    check: (s) => s.trainerAttempts >= 50,
  },

  // ── Engagement ───────────────────────────────────────────────────
  {
    id: "early_bird",
    name: "Pre-departure",
    description: "Review before 7am local.",
    category: "engagement",
    xp: 50,
    icon: "🌅",
    hidden: true,
    check: (s) => s.lastReviewLocalHour !== null && s.lastReviewLocalHour < 7,
  },
  {
    id: "night_owl",
    name: "Mids shift",
    description: "Review after 11pm local.",
    category: "engagement",
    xp: 50,
    icon: "🌙",
    hidden: true,
    check: (s) => s.lastReviewLocalHour !== null && s.lastReviewLocalHour >= 23,
  },
  {
    id: "weekend_warrior",
    name: "Weekend warrior",
    description: "Study on both Saturday and Sunday of the same week.",
    category: "engagement",
    xp: 100,
    icon: "🏖️",
    check: (s) => s.reviewedBothWeekendDays,
  },
  {
    id: "bookmark_collector",
    name: "Pinboard",
    description: "Bookmark 25 cards for targeted review.",
    category: "engagement",
    xp: 75,
    icon: "📌",
    check: (s) => s.bookmarkedCards >= 25,
  },

  // ── Milestones ───────────────────────────────────────────────────
  {
    id: "level_5",
    name: "Trainee level 5",
    description: "Reach level 5.",
    category: "milestone",
    xp: 100,
    icon: "⭐",
    check: (s) => s.xp >= 800,
  },
  {
    id: "level_10",
    name: "Solo cleared",
    description: "Reach level 10.",
    category: "milestone",
    xp: 250,
    icon: "🌟",
    check: (s) => s.xp >= 4050,
  },
  {
    id: "level_25",
    name: "Certified",
    description: "Reach level 25.",
    category: "milestone",
    xp: 1000,
    icon: "✨",
    check: (s) => s.xp >= 28800,
  },
  {
    id: "founder",
    name: "Founder's License",
    description: "Lifetime tier purchased. Thank you.",
    category: "milestone",
    xp: 250,
    icon: "🥇",
    check: (s) => s.tier === "lifetime",
  },
];

/** Map id → Achievement for O(1) lookup. */
const BY_ID: ReadonlyMap<string, Achievement> = new Map(
  ACHIEVEMENTS.map((a) => [a.id, a]),
);

/** Returns the catalog entry for the id, or null if it's been retired. */
export function lookupAchievement(id: string): Achievement | null {
  return BY_ID.get(id) ?? null;
}

/** Returns ids that the user *would* unlock given a stats snapshot,
 *  excluding ones already in `existingUnlocks`. */
export function newlyEarnedIds(
  stats: UserStats,
  existingUnlocks: ReadonlySet<string>,
): string[] {
  const earned: string[] = [];
  for (const a of ACHIEVEMENTS) {
    if (existingUnlocks.has(a.id)) continue;
    if (a.check(stats)) earned.push(a.id);
  }
  return earned;
}

/** Filter the catalog for a category, hiding unrevealed hidden entries. */
export function visibleByCategory(
  unlockedIds: ReadonlySet<string>,
): Record<AchievementCategory, Achievement[]> {
  const out: Record<AchievementCategory, Achievement[]> = {
    streak: [],
    volume: [],
    accuracy: [],
    atsa: [],
    block_prep: [],
    trainer: [],
    engagement: [],
    milestone: [],
  };
  for (const a of ACHIEVEMENTS) {
    if (a.hidden && !unlockedIds.has(a.id)) continue;
    out[a.category].push(a);
  }
  return out;
}
